/**
 * Réglages d'affichage LOCAUX (mode éco) — persistés par appareil. Pensés pour
 * les portables sur batterie : moins de pixels et moins d'images par seconde =
 * moins de charge GPU = moins d'épisodes de bridage énergétique (l'enquête du
 * 19/07 a montré que les saccades venaient de là, pas du jeu). La SIMULATION
 * reste toujours à 60 Hz — le mode éco ne touche que le rendu.
 */

export interface DisplaySettings {
  /** Échelle de résolution du canvas (1 = pleine, 0.75, 0.5). */
  renderScale: number;
  /** Rendu une frame sur deux (~30 img/s) ; la simulation reste à 60 Hz. */
  halfRate: boolean;
  /** Grille hors piste (repère de vitesse) — désactivable pour économiser. */
  grid: boolean;
  /**
   * Adaptation automatique : quand le rendu souffre durablement, le jeu
   * descend tout seul en éco (75 % → 75 % + 30 img/s → 50 % + 30 img/s) puis
   * remonte après une longue accalmie. Ne fait jamais MIEUX que les réglages
   * manuels ci-dessus — il ne peut que descendre en dessous.
   */
  auto: boolean;
  /** Debug : trace le contour de collision (HULL_PATH) sur chaque voiture. */
  hullDebug: boolean;
}

const STORAGE_KEY = 'microligue.display.v1';

export function defaultDisplaySettings(): DisplaySettings {
  return { renderScale: 1, halfRate: false, grid: true, auto: true, hullDebug: false };
}

export function loadDisplaySettings(): DisplaySettings {
  const out = defaultDisplaySettings();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw) as Partial<DisplaySettings>;
    if (parsed.renderScale === 0.5 || parsed.renderScale === 0.75 || parsed.renderScale === 1) {
      out.renderScale = parsed.renderScale;
    }
    if (typeof parsed.halfRate === 'boolean') out.halfRate = parsed.halfRate;
    if (typeof parsed.grid === 'boolean') out.grid = parsed.grid;
    if (typeof parsed.auto === 'boolean') out.auto = parsed.auto;
    if (typeof parsed.hullDebug === 'boolean') out.hullDebug = parsed.hullDebug;
  } catch {
    // stockage corrompu — défauts
  }
  return out;
}

export function saveDisplaySettings(settings: DisplaySettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // stockage indisponible (navigation privée…) — réglages de session
  }
}

/**
 * Gouverneur éco automatique. Jugé une fois par fenêtre FPS (~500 ms) :
 * escalade d'un niveau quand ≥ 4 des 8 dernières fenêtres sont mauvaises,
 * redescend d'un niveau après 30 s sans aucune mauvaise fenêtre, avec 5 s de
 * pose après chaque changement (anti-oscillation : l'effet d'un changement
 * doit se voir dans les mesures avant d'en décider un autre).
 * Niveaux : 0 = réglages de base, 1 = résolution ≤ 75 %, 2 = + 30 img/s,
 * 3 = résolution ≤ 50 % + 30 img/s.
 */
export class AutoEcoGovernor {
  level = 0;
  private badHistory: boolean[] = [];
  private goodSinceMs = 0;
  private lastChangeAt = -Infinity;

  /** Juge une fenêtre ; renvoie le niveau (0-3) après décision. */
  judge(now: number, bad: boolean, windowMs: number): number {
    this.badHistory.push(bad);
    if (this.badHistory.length > 8) this.badHistory.shift();
    this.goodSinceMs = bad ? 0 : this.goodSinceMs + windowMs;
    if (now - this.lastChangeAt < 5_000) return this.level;
    const badCount = this.badHistory.filter(Boolean).length;
    if (badCount >= 4 && this.level < 3) {
      this.level++;
      this.settle(now);
    } else if (this.goodSinceMs >= 30_000 && this.level > 0) {
      this.level--;
      this.settle(now);
    }
    return this.level;
  }

  private settle(now: number): void {
    this.lastChangeAt = now;
    this.badHistory.length = 0;
    this.goodSinceMs = 0;
  }

  /** Adaptation désactivée : tout oublier (niveau neutre). */
  clear(): void {
    this.level = 0;
    this.badHistory.length = 0;
    this.goodSinceMs = 0;
  }
}

/** Résolution effective : le gouverneur ne fait jamais MIEUX que la base. */
export function effectiveRenderScale(settings: DisplaySettings, level: number): number {
  if (!settings.auto) return settings.renderScale;
  if (level >= 3) return Math.min(settings.renderScale, 0.5);
  if (level >= 1) return Math.min(settings.renderScale, 0.75);
  return settings.renderScale;
}

/** Cadence effective : base manuelle OU gouverneur (niveau ≥ 2). */
export function effectiveHalfRate(settings: DisplaySettings, level: number): boolean {
  return settings.halfRate || (settings.auto && level >= 2);
}
